import { Card } from '@radix-ui/themes'
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL

export type MessageDialogType = 'success' | 'error' | 'info'

interface MessageDialogProps {
  open: boolean
  onClose: () => void
  type?: MessageDialogType
  title: string
  description: string
  /** Contenido extra debajo de la descripción (ej. hashes de transacción) */
  children?: ReactNode
  portalButton?: boolean
}

const ICONS: Record<MessageDialogType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info
}

const MessageDialog = ({
  open,
  onClose,
  type = 'info',
  title,
  description,
  children,
  portalButton
}: MessageDialogProps) => {
  const { t } = useTranslation()
  const Icon = ICONS[type]

  return (
    <div
      className="fixed inset-0 bg-opacity-50 backdrop-blur-xs overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center px-4">
        <Card className="shadow-xl bg-card w-[90%] sm:w-1/2 lg:w-1/3 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-text-secondary hover:text-text-main transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col gap-2 items-center p-6">
            <div className="rounded-full p-3 w-fit bg-card-e2">
              <Icon
                className="w-8 h-8"
                style={{
                  color:
                    type === 'success'
                      ? 'var(--status-success-text)'
                      : type === 'error'
                        ? 'var(--status-error-text)'
                        : 'var(--color-primary)'
                }}
              />
            </div>
            <h3 className="text-text-primary font-semibold text-xl text-center">
              {title}
            </h3>
            <p className="text-text-secondary text-sm text-center">
              {description}
            </p>
            {children}
            {portalButton && (
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gradient-fill-secondary shadow-sm py-3 px-6 rounded-full font-bold mt-2"
              >
                {t('messageDialog.goToPortal')}
              </a>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}

export default MessageDialog
